/**
 * Maps screen/page names to badge keys used in Redux UI state
 * Badge keys are lowercase and match the menu item screen names
 */

const SCREEN_TO_BADGE_MAP: Record<string, string> = {
	// Data screens
	DataScreen: 'data',
	DataBrowser: 'data',
	DataHandler: 'data',
	TimeLineData: 'data',
	ActionHandler: 'data',
	PublicData: 'data',
	CreateData: 'data',
	DataMenu: 'data',

	// Project Management
	ProjectManagement: 'projectmanagement',
	Projects: 'projectmanagement',
	IndividualProjectPage: 'projectmanagement',

	// Calendar/Organiser
	Calendar: 'organiser',
	CalendarScreen: 'organiser',
	MeetingPage: 'organiser',

	// Sales
	Sales: 'sales',
	SalesScreen: 'sales',

	// Reports
	Reports: 'reports',
	ReportsScreen: 'reports',

	// Company
	Company: 'company',
	CompanyProfile: 'company',

	// Knowledge
	Knowledge: 'knowledge',
	KnowledgeScreen: 'knowledge',

	// Integrations
	Integrations: 'integrations',
	IntegrationScreen: 'integrations',

	// Settings
	Settings: 'settings',
	SettingsMenu: 'settings',
	Profile: 'settings',
	Context: 'settings',

	// Academy
	Academy: 'academy',
	AcademyScreen: 'academy',

	// AI/Easy Mode
	EasyModeScreen: 'ai',
	HelpSidePanel: 'ai',
	HelpModal: 'ai',

	// Welcome
	Welcome: 'welcome',
	WelcomeScreen: 'welcome',

	// Users
	Users: 'users',
	UsersScreen: 'users',

	// Messages
	Message: 'data', // Messages might use data badge
	MessageList: 'data',
	MessageScreen: 'data',
};

/**
 * Maps a screen/page name to its corresponding badge key
 * @param screenName - The screen name (e.g., 'DataScreen', 'EasyModeScreen')
 * @returns The badge key (e.g., 'data', 'ai') or null if not found
 */
export function mapScreenToBadgeKey(
	screenName: string | undefined
): string | null {
	if (!screenName) return null;

	// Direct mapping
	if (SCREEN_TO_BADGE_MAP[screenName]) {
		return SCREEN_TO_BADGE_MAP[screenName];
	}

	// Try lowercase match
	const lowerScreen = screenName.toLowerCase();
	if (SCREEN_TO_BADGE_MAP[lowerScreen]) {
		return SCREEN_TO_BADGE_MAP[lowerScreen];
	}

	// Try partial matching for common patterns
	if (lowerScreen.includes('data')) return 'data';
	if (lowerScreen.includes('project')) return 'projectmanagement';
	if (lowerScreen.includes('calendar') || lowerScreen.includes('meeting'))
		return 'organiser';
	if (lowerScreen.includes('sales')) return 'sales';
	if (lowerScreen.includes('report')) return 'reports';
	if (lowerScreen.includes('company')) return 'company';
	if (lowerScreen.includes('knowledge')) return 'knowledge';
	if (lowerScreen.includes('integration')) return 'integrations';
	if (lowerScreen.includes('setting')) return 'settings';
	if (lowerScreen.includes('academy')) return 'academy';
	if (
		lowerScreen.includes('easy') ||
		lowerScreen.includes('help') ||
		lowerScreen.includes('ai')
	)
		return 'ai';
	if (lowerScreen.includes('welcome')) return 'welcome';
	if (lowerScreen.includes('user')) return 'users';

	return null;
}

/**
 * Gets all available badge keys
 */
export function getAvailableBadgeKeys(): string[] {
	return [
		'welcome',
		'data',
		'sales',
		'projectmanagement',
		'organiser',
		'reports',
		'company',
		'knowledge',
		'integrations',
		'settings',
		'academy',
		'ai',
		'users',
	];
}
